<script setup>
import { ref, computed, onMounted } from 'vue';
import { conversionsApi } from 'dashboard/api/conversions';
import { useMapGetter } from 'dashboard/composables/store';

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const enabled = computed(() =>
  isFeatureEnabled.value(accountId.value, 'meta_campaign_tracking')
);

const loading = ref(false);
const data = ref(null);
const startDate = ref('');
const endDate = ref('');
const source = ref('all');
const pipelineId = ref('all');
const expanded = ref({});

const mappings = ref([]);
const showConfig = ref(false);
const newMapping = ref({
  match_type: 'ref',
  match_value: '',
  campaign_name: '',
  creative_name: '',
});

const brl = v =>
  v == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(v);
const pct = v => (v == null ? '—' : `${Math.round(v * 100)}%`);

const stages = computed(() => data.value?.stages || []);
const sources = computed(() => data.value?.sources || []);
const organic = computed(() => data.value?.organic);
const totals = computed(() => data.value?.totals || {});
const pipelines = computed(() => data.value?.pipelines || []);

// Agrupa as etapas de um anúncio por FUNIL (pipeline) e esconde os funis em que
// o anúncio não tem nenhum lead — evita a lista corrida das 29 etapas sem contexto.
const funnelGroups = src => {
  const stageMap = {};
  stages.value.forEach(st => {
    stageMap[st.stage_id] = st;
  });
  const pipeName = {};
  pipelines.value.forEach(p => {
    pipeName[p.id] = p.name;
  });
  const groups = {};
  src.funnel.forEach(f => {
    const st = stageMap[f.stage_id];
    if (!st) return;
    const pid = st.pipeline_id;
    if (!groups[pid]) {
      groups[pid] = {
        pipeline_id: pid,
        pipeline_name: pipeName[pid] || `Funil ${pid}`,
        stages: [],
      };
    }
    groups[pid].stages.push({
      stage_id: f.stage_id,
      name: st.stage_name,
      leads: f.leads,
    });
  });
  return Object.values(groups)
    .map(g => ({ ...g, total: g.stages.reduce((a, s) => a + s.leads, 0) }))
    .filter(g => g.total > 0)
    .sort((a, b) => b.total - a.total);
};
const groupMax = g => Math.max(1, ...g.stages.map(s => s.leads));

const fetchFunnel = async () => {
  loading.value = true;
  try {
    const params = {};
    if (startDate.value) params.start_date = startDate.value;
    if (endDate.value) params.end_date = endDate.value;
    if (source.value !== 'all') params.source = source.value;
    if (pipelineId.value !== 'all') params.pipeline_id = pipelineId.value;
    const { data: res } = await conversionsApi.campaignFunnel(params);
    data.value = res;
  } finally {
    loading.value = false;
  }
};

const fetchMappings = async () => {
  const { data: res } = await conversionsApi.campaignMappings();
  mappings.value = res || [];
};

const addMapping = async () => {
  if (!newMapping.value.match_value) return;
  await conversionsApi.createCampaignMapping(newMapping.value);
  newMapping.value = {
    match_type: 'ref',
    match_value: '',
    campaign_name: '',
    creative_name: '',
  };
  await fetchMappings();
  await fetchFunnel();
};

const removeMapping = async id => {
  await conversionsApi.deleteCampaignMapping(id);
  await fetchMappings();
  await fetchFunnel();
};

// --- Conexão com a Meta (Marketing API — nomes reais de campanha/conjunto/anúncio + gasto) ---
const meta = ref(null);
const showMeta = ref(false);
const adAccountInput = ref('');
const syncing = ref(false);
const syncMsg = ref('');

const fetchMeta = async () => {
  const { data: res } = await conversionsApi.metaConnection();
  meta.value = res;
  adAccountInput.value = (res?.ad_account_ids || []).join(', ');
};

const saveAdAccount = async () => {
  const { data: res } = await conversionsApi.updateMetaConnection(
    adAccountInput.value.trim()
  );
  meta.value = res;
};

const runSync = async () => {
  syncing.value = true;
  syncMsg.value = '';
  try {
    const { data: res } = await conversionsApi.syncMeta();
    meta.value = { ...meta.value, ...res };
    syncMsg.value = `Sincronizado: ${res.resolved} anúncio(s), ${res.spend_rows} dia(s) de gasto.`;
    await fetchFunnel();
  } catch (e) {
    syncMsg.value =
      e?.response?.data?.error || 'Falha ao sincronizar com a Meta.';
  } finally {
    syncing.value = false;
  }
};

const toggle = key => {
  expanded.value = { ...expanded.value, [key]: !expanded.value[key] };
};

onMounted(() => {
  if (enabled.value) {
    fetchFunnel();
    fetchMappings();
    fetchMeta();
  }
});
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-n-background">
    <div v-if="!enabled" class="p-8 text-n-slate-11">
      O Rastreio de Anúncios não está habilitado para esta conta.
    </div>

    <div v-else class="flex flex-col gap-4 p-6">
      <!-- Header + filtros -->
      <div class="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1
            class="text-xl font-semibold text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-radar" /> Rastreio de Anúncios
          </h1>
          <p class="text-sm text-n-slate-11">
            Desempenho por anúncio/criativo — do primeiro contato à venda ganha.
          </p>
        </div>
        <div class="flex items-end gap-2 flex-wrap">
          <label class="flex flex-col text-xs text-n-slate-11">
            De
            <input
              v-model="startDate"
              type="date"
              class="mt-1 px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12"
            />
          </label>
          <label class="flex flex-col text-xs text-n-slate-11">
            Até
            <input
              v-model="endDate"
              type="date"
              class="mt-1 px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12"
            />
          </label>
          <label class="flex flex-col text-xs text-n-slate-11">
            Funil
            <select
              v-model="pipelineId"
              class="mt-1 px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12"
              @change="fetchFunnel"
            >
              <option value="all">Todos os funis</option>
              <option v-for="p in pipelines" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
          </label>
          <label class="flex flex-col text-xs text-n-slate-11">
            Origem
            <select
              v-model="source"
              class="mt-1 px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12"
            >
              <option value="all">Tudo</option>
              <option value="paid">Pago</option>
              <option value="organic">Orgânico</option>
            </select>
          </label>
          <button
            class="px-3 py-1.5 rounded bg-n-brand text-white text-sm hover:opacity-90"
            @click="fetchFunnel"
          >
            Atualizar
          </button>
          <button
            class="px-3 py-1.5 rounded border border-n-weak text-n-slate-11 text-sm hover:bg-n-alpha-1"
            @click="showConfig = !showConfig"
          >
            <span class="i-lucide-settings" /> Nomes
          </button>
          <button
            class="px-3 py-1.5 rounded border text-sm hover:bg-n-alpha-1"
            :class="
              meta?.configured
                ? 'border-n-teal-7 text-n-teal-11'
                : 'border-n-weak text-n-slate-11'
            "
            @click="showMeta = !showMeta"
          >
            <span class="i-lucide-plug-zap" />
            Meta {{ meta?.configured ? '· conectado' : '' }}
          </button>
        </div>
      </div>

      <!-- KPI cards -->
      <div class="grid grid-cols-2 md:grid-cols-7 gap-3">
        <div class="p-3 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="text-xs text-n-slate-11">Leads (pagos)</div>
          <div class="text-lg font-semibold text-n-slate-12">
            {{ totals.leads ?? 0 }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="text-xs text-n-slate-11">Leads orgânicos</div>
          <div class="text-lg font-semibold text-n-slate-12">
            {{ totals.organic_leads ?? 0 }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="text-xs text-n-slate-11">Conversões</div>
          <div class="text-lg font-semibold text-n-slate-12">
            {{ totals.conversions ?? 0 }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="text-xs text-n-slate-11">Receita</div>
          <div class="text-lg font-semibold text-n-slate-12">
            {{ brl(totals.revenue) }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="text-xs text-n-slate-11">Gasto (Meta)</div>
          <div class="text-lg font-semibold text-n-slate-12">
            {{ brl(totals.spend) }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="text-xs text-n-slate-11">ROAS</div>
          <div class="text-lg font-semibold text-n-slate-12">
            {{ totals.roas == null ? '—' : `${totals.roas}x` }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="text-xs text-n-slate-11">Cobertura de atribuição</div>
          <div class="text-lg font-semibold text-n-slate-12">
            {{ pct(data?.attribution_coverage) }}
          </div>
        </div>
      </div>

      <!-- Conexão com a Meta (Marketing API) -->
      <div
        v-if="showMeta"
        class="p-4 rounded-lg border border-n-weak bg-n-solid-1 flex flex-col gap-3"
      >
        <div
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <span class="i-lucide-plug-zap" />
          Conexão com a Meta (Gerenciador de Anúncios)
          <span
            class="text-xs px-2 py-0.5 rounded-full"
            :class="
              meta?.configured
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{ meta?.configured ? 'Conectado' : 'Não conectado' }}
          </span>
        </div>
        <p class="text-xs text-n-slate-11">
          Puxa os nomes reais de campanha / conjunto / anúncio + o gasto direto
          da sua conta de anúncios, cruzando pelo <b>ad_id</b> que já capturamos
          de cada lead. O token de acesso (system user, ads_read) é configurado
          no servidor.
        </p>
        <div class="flex flex-wrap items-end gap-2">
          <label class="flex flex-col text-xs text-n-slate-11">
            IDs das contas de anúncio (act_…, várias por vírgula)
            <input
              v-model="adAccountInput"
              placeholder="123456…, 789012…"
              class="mt-1 px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12 text-sm w-72"
            />
          </label>
          <button
            class="px-3 py-1.5 rounded border border-n-weak text-n-slate-11 text-sm hover:bg-n-alpha-1"
            @click="saveAdAccount"
          >
            Salvar contas
          </button>
          <button
            class="px-3 py-1.5 rounded bg-n-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
            :disabled="syncing || !meta?.configured"
            @click="runSync"
          >
            <span
              :class="
                syncing
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-refresh-cw'
              "
            />
            {{ syncing ? 'Sincronizando…' : 'Sincronizar com a Meta' }}
          </button>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <div class="text-n-slate-11">
            Token no servidor:
            <span
              :class="
                meta?.token_present ? 'text-n-teal-11' : 'text-n-amber-11'
              "
              >{{ meta?.token_present ? 'sim' : 'não' }}</span
            >
          </div>
          <div class="text-n-slate-11">
            Contas conectadas:
            <span class="text-n-slate-12">{{ meta?.accounts_count ?? 0 }}</span>
          </div>
          <div class="text-n-slate-11">
            Anúncios capturados:
            <span class="text-n-slate-12">{{ meta?.captured_ads ?? 0 }}</span>
          </div>
          <div class="text-n-slate-11">
            Anúncios resolvidos:
            <span class="text-n-slate-12">{{ meta?.resolved_ads ?? 0 }}</span>
          </div>
          <div class="text-n-slate-11">
            Dias de gasto:
            <span class="text-n-slate-12">{{ meta?.spend_days ?? 0 }}</span>
          </div>
        </div>
        <p v-if="syncMsg" class="text-xs text-n-slate-11">{{ syncMsg }}</p>
        <p v-if="!meta?.token_present" class="text-xs text-n-amber-11">
          O token de acesso ainda não foi configurado no servidor. Sem ele, o
          painel usa a legenda do anúncio como nome (fallback) e não mostra
          gasto / CPL / ROAS.
        </p>
      </div>

      <!-- Config: mapa de nomes -->
      <div
        v-if="showConfig"
        class="p-4 rounded-lg border border-n-weak bg-n-solid-1 flex flex-col gap-3"
      >
        <div class="text-sm font-medium text-n-slate-12">
          Nomes amigáveis de campanha (ref / ad_id → nome)
        </div>
        <div class="flex flex-wrap items-end gap-2">
          <select
            v-model="newMapping.match_type"
            class="px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12 text-sm"
          >
            <option value="ref">ref</option>
            <option value="ad_id">ad_id</option>
          </select>
          <input
            v-model="newMapping.match_value"
            placeholder="valor (ex.: vb_natal24_video1)"
            class="px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12 text-sm"
          />
          <input
            v-model="newMapping.campaign_name"
            placeholder="Nome da campanha"
            class="px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12 text-sm"
          />
          <input
            v-model="newMapping.creative_name"
            placeholder="Nome do criativo"
            class="px-2 py-1 rounded border border-n-weak bg-n-background text-n-slate-12 text-sm"
          />
          <button
            class="px-3 py-1 rounded bg-n-brand text-white text-sm"
            @click="addMapping"
          >
            Adicionar
          </button>
        </div>
        <div class="flex flex-col gap-1">
          <div
            v-for="m in mappings"
            :key="m.id"
            class="flex items-center gap-2 text-sm text-n-slate-11"
          >
            <span class="font-mono text-xs">{{ m.match_type }}:{{ m.match_value }}</span>
            <span class="i-lucide-arrow-right text-xs" />
            <span class="text-n-slate-12">{{ m.campaign_name }}
              {{ m.creative_name ? '· ' + m.creative_name : '' }}</span>
            <button
              class="i-lucide-trash-2 text-n-ruby-9 ml-1"
              @click="removeMapping(m.id)"
            />
          </div>
        </div>
      </div>

      <!-- Tabela de fontes -->
      <div class="rounded-lg border border-n-weak overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-n-solid-2 text-n-slate-11">
            <tr>
              <th class="text-left p-2 font-medium">
                Campanha / Conjunto / Anúncio
              </th>
              <th class="text-right p-2 font-medium">Leads</th>
              <th class="text-right p-2 font-medium">Conversões</th>
              <th class="text-right p-2 font-medium">Receita</th>
              <th class="text-right p-2 font-medium">Gasto</th>
              <th class="text-right p-2 font-medium">CPL</th>
              <th class="text-right p-2 font-medium">ROAS</th>
              <th class="w-8" />
            </tr>
          </thead>
          <tbody>
            <template v-for="s in sources" :key="s.source_key">
              <tr class="border-t border-n-weak hover:bg-n-alpha-1">
                <td class="p-2 text-n-slate-12">
                  <div class="font-medium">{{ s.display_name }}</div>
                  <div
                    v-if="s.adset_name || s.ad_name"
                    class="text-n-slate-11 text-xs"
                  >
                    <span v-if="s.adset_name">{{ s.adset_name }}</span>
                    <span v-if="s.adset_name && s.ad_name"> · </span>
                    <span v-if="s.ad_name">{{ s.ad_name }}</span>
                  </div>
                  <div
                    v-else-if="
                      s.creative_name && s.creative_name !== s.display_name
                    "
                    class="text-n-slate-11 text-xs"
                  >
                    {{ s.creative_name }}
                  </div>
                </td>
                <td class="p-2 text-right text-n-slate-12">{{ s.leads }}</td>
                <td class="p-2 text-right text-n-slate-12">
                  {{ s.conversions }}
                </td>
                <td class="p-2 text-right text-n-slate-12">
                  {{ brl(s.revenue) }}
                </td>
                <td class="p-2 text-right text-n-slate-12">
                  {{ brl(s.spend) }}
                </td>
                <td class="p-2 text-right text-n-slate-11">
                  {{ s.cpl == null ? '—' : brl(s.cpl) }}
                </td>
                <td class="p-2 text-right text-n-slate-11">
                  {{ s.roas == null ? '—' : `${s.roas}x` }}
                </td>
                <td class="p-2 text-right">
                  <button
                    class="text-n-slate-11"
                    :class="
                      expanded[s.source_key]
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                    @click="toggle(s.source_key)"
                  />
                </td>
              </tr>
              <tr v-if="expanded[s.source_key]" class="bg-n-solid-1">
                <td colspan="8" class="p-3">
                  <p
                    v-if="!funnelGroups(s).length"
                    class="text-xs italic text-n-slate-11"
                  >
                    Este anúncio ainda não tem leads em nenhuma etapa de funil.
                  </p>
                  <div
                    v-for="g in funnelGroups(s)"
                    :key="g.pipeline_id"
                    class="flex flex-col gap-1 mb-4 last:mb-0"
                  >
                    <div
                      class="flex items-center gap-1 mb-0.5 text-xs font-semibold text-n-slate-12"
                    >
                      <span class="i-lucide-filter size-3.5" />
                      Funil: {{ g.pipeline_name }}
                      <span class="font-normal text-n-slate-10"
                        >· {{ g.total }} leads</span
                      >
                    </div>
                    <div
                      v-for="f in g.stages"
                      :key="f.stage_id"
                      class="flex items-center gap-2"
                    >
                      <div class="w-40 text-xs text-n-slate-11 truncate">
                        {{ f.name }}
                      </div>
                      <div
                        class="flex-1 bg-n-alpha-1 rounded h-4 overflow-hidden"
                      >
                        <div
                          class="h-4 bg-n-brand rounded"
                          :style="{
                            width: `${Math.round((f.leads / groupMax(g)) * 100)}%`,
                          }"
                        />
                      </div>
                      <div class="w-10 text-xs text-right text-n-slate-12">
                        {{ f.leads }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Orgânico -->
            <tr v-if="organic" class="border-t border-n-weak bg-n-solid-1/50">
              <td class="p-2 text-n-slate-11 italic">
                <span class="i-lucide-sprout" /> Sem origem de anúncio
                (orgânico/direto)
              </td>
              <td class="p-2 text-right text-n-slate-11">
                {{ organic.leads }}
              </td>
              <td class="p-2 text-right text-n-slate-11">
                {{ organic.conversions }}
              </td>
              <td class="p-2 text-right text-n-slate-11">
                {{ brl(organic.revenue) }}
              </td>
              <td class="p-2 text-right text-n-slate-11">—</td>
              <td class="p-2 text-right text-n-slate-11">—</td>
              <td class="p-2 text-right text-n-slate-11">—</td>
              <td />
            </tr>

            <tr v-if="!sources.length && !loading">
              <td colspan="8" class="p-6 text-center text-n-slate-11">
                Nenhum lead de anúncio no período. Quando um lead chegar por um
                anúncio Click-to-WhatsApp, ele aparece aqui.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="text-xs text-n-slate-10">
        Nomes reais de campanha/conjunto/anúncio e gasto (CPL/CAC/ROAS) vêm da
        conta de anúncios da Meta — conecte no botão <b>Meta</b> acima. Sem
        conexão, o painel usa a legenda do anúncio como nome e entrega funil +
        receita por anúncio.
      </p>
    </div>
  </div>
</template>
